import React, { useCallback, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box, Typography } from "@mui/material";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

function RichTextEditor({ value, onChange }) {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image"],
        ["clean"],
      ],
    }),
    []
  );

  const formats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "blockquote",
      "code-block",
      "link",
      "image",
    ],
    []
  );

  const handleChange = useCallback(
    (html) => {
      const text = stripHtml(html).trim();
      const normalized =
        !text || html === "<p><br></p>" || html === "<p></p>" ? "" : html;
      onChange(normalized);
    },
    [onChange]
  );

  const text = stripHtml(value || "").trim();
  const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
  const readTime = Math.max(1, Math.ceil(words / 200));

  return (
    <Box
      sx={{
        border: "1.5px solid",
        borderColor: "#E5E7EB",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        transition: "all 0.2s ease",
        "& .ql-toolbar": {
          border: "none",
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: "#F8F9FB",
        },
        "& .ql-container": {
          border: "none",
          minHeight: "260px",
          maxHeight: "520px",
          fontSize: "1rem",
        },
        "& .ql-editor": {
          maxHeight: "520px",
          overflowY: "auto",
        },
      }}
    >
      <ReactQuill
        theme="snow"
        value={value || ""}
        onChange={handleChange}
        modules={modules}
        formats={formats}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          backgroundColor: "#F8F9FB",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500 }}>
          Word count: {words} words
        </Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
          Reading time: {readTime} min
        </Typography>
      </Box>
    </Box>
  );
}

export default RichTextEditor;
